// src/components/Profile/ProfileSidebar.js
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import Loader from './Loader';

const ProfileSidebar = ({ isOpen, onClose }) => {
	const [userData, setUserData] = useState(null);
	const [loading, setLoading] = useState(true);
	const location = useLocation();

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const token = Cookies.get("ecom_user_token");
				const response = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-by-id`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				setUserData(response?.data?.user);
			} catch (error) {
				console.error("Failed to fetch user data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchUserData();
	}, []);

	const handleLinkClick = () => {
		if (window.innerWidth < 1024) {
			onClose();
		}
	};

	const menuItems = [
		{ path: '/profile', label: 'Profile', icon: '👤' },
		{ path: '/profile/addresses', label: 'Addresses', icon: '📍' },
		{ path: '/profile/orders', label: 'My Orders', icon: '📦' },
	];

	const getInitials = (name) => {
		return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
	};

	if (loading) {
		return (
			<div className="fixed lg:static inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-white to-gray-50 rounded-r-2xl shadow-2xl overflow-hidden">
				<div className="p-6 h-full flex items-center justify-center">
					<div className="animate-pulse space-y-4">
						<div className="rounded-full bg-gray-200 h-24 w-24 mx-auto"></div>
						<div className="h-4 bg-gray-200 rounded w-32"></div>
						<div className="h-3 bg-gray-200 rounded w-40"></div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			{/* Overlay */}
			<div
				className={`fixed inset-0 bg-black transition-all duration-300 z-40 lg:hidden ${isOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'
					}`}
				onClick={onClose}
			></div>

			{/* Sidebar */}
			<aside
				className={`fixed lg:relative inset-y-0 left-0 z-50 w-72 bg-[#fdfaf6] border-r border-[#eaddcf] rounded-r-3xl lg:rounded-none shadow-2xl lg:shadow-none transform transition-all duration-300 ease-out lg:translate-x-0 ${isOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
					}`}
				style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/paper-fibers.png')" }}
			>
				{/* Header - Fixed Height */}
				<div className="h-16 p-4 border-b border-[#eaddcf] flex justify-between items-center lg:hidden">
					<h3 className="text-lg font-serif font-bold text-[#5d4037]">Menu</h3>
					<button
						onClick={onClose}
						className="p-2 rounded-full hover:bg-[#efe0d0] transition-colors duration-200"
						aria-label="Close menu"
					>
						<svg className="h-6 w-6 text-[#8d6e63]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Inner Content - Flexible Height with Scroll */}
				<div className="flex flex-col h-[calc(100%-4rem)] lg:h-full">
					<div className="p-6 overflow-y-auto flex-grow custom-scrollbar">

						{/* Artisan User Profile Card */}
						<div className="bg-[#5d4037] bg-[url('https://www.transparenttextures.com/patterns/wood-pattern.png')] rounded-2xl p-6 mb-8 shadow-inner border-b-4 border-[#3e2723]">
							<div className="flex flex-col items-center">
								<div className="relative mb-4">
									<div className="w-20 h-20 rounded-full bg-[#fdfaf6] p-1 flex items-center justify-center shadow-md">
										<div className="w-full h-full rounded-full bg-[#a1887f] flex items-center justify-center border-2 border-[#8d6e63] border-dashed">
											<span className="text-[#fdfaf6] text-2xl font-serif font-bold">
												{getInitials(userData?.name)}
											</span>
										</div>
									</div>
									<div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#8bc34a] rounded-full border-2 border-[#5d4037]"></div>
								</div>

								<h2 className="text-xl font-serif font-bold text-[#fdfaf6] text-center mb-1 leading-tight">
									{userData?.name || 'Artisan User'}
								</h2>
								<p className="text-[#d7ccc8] text-sm text-center truncate max-w-full italic">
									{userData?.email || 'user@example.com'}
								</p>
								<div className="flex items-center justify-center mt-4 p-3 w-full bg-[#3e2723]/40 rounded-xl shadow-inner border-t border-[#6d4c41]/50">
									<svg className="w-4 h-4 mr-2 text-[#efe0d0] drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
										<path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
									</svg>
									<span className="text-[#fdfaf6] text-xs font-serif font-bold tracking-[0.15em] drop-shadow-md">
										{userData?.phone || 'NO PHONE REGISTERED'}
									</span>
								</div>
							</div>
						</div>

						{/* Navigation Menu */}
						<nav className="space-y-2 pb-6">
							{menuItems.map((item) => {
								const isActive = location.pathname === item.path ||
									(item.path !== '/profile' && location.pathname.includes(item.path));

								return (
									<Link
										key={item.path}
										to={item.path}
										onClick={handleLinkClick}
										className={`group flex items-center px-5 py-3 rounded-lg transition-all duration-300 ${isActive
											? 'bg-[#efe0d0] text-[#5d4037] shadow-sm border-l-4 border-[#8d6e63]'
											: 'hover:bg-[#f5ebe0] text-[#6d4c41] hover:text-[#5d4037]'
											}`}
									>
										<span className={`text-xl mr-4 transition-transform duration-300 group-hover:rotate-12 ${isActive ? 'text-[#8d6e63]' : 'opacity-70'}`}>
											{item.icon}
										</span>
										<span className="font-serif font-semibold tracking-wide">{item.label}</span>
										{isActive && (
											<div className="ml-auto w-1.5 h-1.5 bg-[#8d6e63] rounded-full"></div>
										)}
									</Link>
								);
							})}
						</nav>
					</div>

					{/* Optional: Artisan Footer / Logout Section */}
					<div className="p-4 border-t border-[#eaddcf] bg-[#fdfaf6]/80 backdrop-blur-sm">
						<p className="text-[10px] text-center uppercase tracking-[0.2em] text-[#a1887f] font-bold">
							Handcrafted Quality
						</p>
					</div>
				</div>
			</aside>
		</>
	);
};

export default ProfileSidebar;
